import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { MessageSquare, CheckCircle, Clock, AlertTriangle, User } from "lucide-react";
import { toast } from "sonner";
import { formatFullDateWithTimezone } from "@/utils/timezoneUtils";

interface FeedbackItem {
  id: string;
  title: string;
  category: string;
  message: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  admin_response: string | null;
}

interface UserReport {
  id: string;
  reported_user_id: string;
  category: string;
  description: string;
  created_at: string;
  resolved_at: string | null;
  admin_response: string | null;
  reported_user?: {
    first_name: string | null;
    last_name: string | null;
  };
}

const REPORT_CATEGORY_LABELS: Record<string, string> = {
  harassment: "Harassment",
  inappropriate_content: "Inappropriate Content",
  fraud: "Fraud/Scam",
  off_platform_transaction: "Off-Platform Transaction",
  academic_dishonesty: "Academic Dishonesty",
  impersonation: "Impersonation",
  spam: "Spam",
  safety_concern: "Safety Concern",
  other: "Other",
};

const UserFeedbackList = () => {
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [reports, setReports] = useState<UserReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("feedback");

  const fetchUserFeedback = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("feedback")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setFeedback(data || []);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast.error("Failed to load your feedback");
    }
  };

  const fetchUserReports = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("user_reports")
        .select("*")
        .eq("reporter_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch reported user profiles
      const reportsWithProfiles: UserReport[] = [];
      for (const report of data || []) {
        const { data: reportedUser } = await supabase.rpc("get_public_profile", {
          profile_user_id: report.reported_user_id,
        });
        reportsWithProfiles.push({
          ...report,
          reported_user: reportedUser?.[0] || null,
        });
      }

      setReports(reportsWithProfiles);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchUserFeedback(), fetchUserReports()]);
      setLoading(false);
    };
    fetchAll();
  }, [user]);

  const formatDate = (dateString: string) => {
    return formatFullDateWithTimezone(dateString);
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      "Bug Report": "bg-red-500/10 text-red-500 dark:bg-red-500/20",
      "Feature Request": "bg-blue-500/10 text-blue-500 dark:bg-blue-500/20",
      "General": "bg-muted text-muted-foreground",
      "UI/UX": "bg-purple-500/10 text-purple-500 dark:bg-purple-500/20",
      "Performance": "bg-orange-500/10 text-orange-500 dark:bg-orange-500/20",
    };
    return colors[category] || "bg-muted text-muted-foreground";
  };

  const getReportedUserName = (profile: { first_name: string | null; last_name: string | null } | undefined) => {
    if (!profile) return "Unknown User";
    const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ");
    return name || "Unknown User";
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const hasReports = reports.length > 0;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full grid-cols-2 mb-4">
        <TabsTrigger value="feedback" className="text-sm">
          <MessageSquare className="h-3 w-3 mr-1" />
          Feedback ({feedback.length})
        </TabsTrigger>
        <TabsTrigger value="reports" className="text-sm">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Reports ({reports.length})
        </TabsTrigger>
      </TabsList>

      <TabsContent value="feedback">
        {feedback.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <MessageSquare className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="font-poppins text-xl font-semibold text-foreground mb-2">
                No Feedback Yet
              </h3>
              <p className="text-muted-foreground font-inter">
                You haven't submitted any feedback yet. Share your thoughts to help us improve!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {feedback.map((item) => (
              <Card key={item.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="font-poppins text-lg">{item.title}</CardTitle>
                        {item.resolved_at ? (
                          <Badge variant="success" className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Resolved
                          </Badge>
                        ) : (
                          <Badge variant="warning" className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Pending
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="font-inter">
                        Submitted on {formatDate(item.created_at)}
                        {item.resolved_at && (
                          <span className="ml-2">
                            • Resolved on {formatDate(item.resolved_at)}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    <Badge className={getCategoryColor(item.category)}>
                      {item.category}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2 font-inter text-foreground">Your Message:</h4>
                    <p className="text-foreground font-inter whitespace-pre-wrap bg-muted p-4 rounded">
                      {item.message}
                    </p>
                  </div>
                  
                  {item.admin_response && (
                    <div>
                      <h4 className="font-semibold mb-2 font-inter text-foreground">Admin Response:</h4>
                      <p className="text-foreground font-inter whitespace-pre-wrap bg-blue-500/10 dark:bg-blue-500/20 p-4 rounded border-l-4 border-l-blue-500">
                        {item.admin_response}
                      </p>
                    </div>
                  )}
                  
                  {!item.resolved_at && !item.admin_response && (
                    <div className="bg-yellow-500/10 dark:bg-yellow-500/20 p-4 rounded border-l-4 border-l-yellow-500">
                      <p className="text-yellow-600 dark:text-yellow-400 font-inter text-sm">
                        Your feedback is being reviewed. We'll respond as soon as possible.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="reports">
        {reports.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="font-poppins text-xl font-semibold text-foreground mb-2">
                No Reports Submitted
              </h3>
              <p className="text-muted-foreground font-inter">
                You haven't reported any users yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <Card key={report.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="font-poppins text-base flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Report: {getReportedUserName(report.reported_user)}
                        </CardTitle>
                        {report.resolved_at ? (
                          <Badge variant="success" className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Resolved
                          </Badge>
                        ) : (
                          <Badge variant="warning" className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Pending
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="font-inter">
                        Submitted on {formatDate(report.created_at)}
                        {report.resolved_at && (
                          <span className="ml-2">
                            • Resolved on {formatDate(report.resolved_at)}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    <Badge variant="outline">
                      {REPORT_CATEGORY_LABELS[report.category] || report.category}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2 font-inter text-sm text-foreground">Your Report:</h4>
                    <p className="text-foreground font-inter whitespace-pre-wrap bg-muted p-4 rounded text-sm">
                      {report.description}
                    </p>
                  </div>
                  
                  {report.admin_response && (
                    <div>
                      <h4 className="font-semibold mb-2 font-inter text-sm text-foreground">Admin Response:</h4>
                      <p className="text-foreground font-inter whitespace-pre-wrap bg-green-500/10 dark:bg-green-500/20 p-4 rounded border-l-4 border-l-green-500 text-sm">
                        {report.admin_response}
                      </p>
                    </div>
                  )}
                  
                  {!report.resolved_at && !report.admin_response && (
                    <div className="bg-yellow-500/10 dark:bg-yellow-500/20 p-4 rounded border-l-4 border-l-yellow-500">
                      <p className="text-yellow-600 dark:text-yellow-400 font-inter text-sm">
                        Your report is being reviewed. We'll take appropriate action if necessary.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};

export default UserFeedbackList;
